<template>
  <div>
    <Loader :isVisible="isLoading" />
    <div class="images-wrapper">
      <div
        v-if="assets.length === 0 && !isLoading"
        class="no-images"
      >
        <Loader
          :isVisible="assets.length === 0 && !isLoading"
          className="media-loader"
        />
        No images to display
      </div>
      <div
        v-if="shouldWarnImagesTab"
        class="unsaved-changes-warning"
      >
        <inline-svg
          :src="iconWarning"
          class="svg-icon"
        ></inline-svg>
        Changes won’t be saved unless you click Apply
      </div>
      <div
        v-for="imageAsset in assets"
        :key="imageAsset.id"
        :ref="`imageAssetItem_${imageAsset.id}`"
        class="image-container"
      >
        <Loader
          :isVisible="assetItemInProcessing === imageAsset.id"
          className="assets-loader"
        />
        <AssetImageUploader
          ref="assetImage"
          :disabled="!imageAsset.isAnyPoster"
          :isPosterInspector="true"
          imageType="Mezzanine Image"
          :source="imageAsset"
          :isVirtualAsset="imageAsset.isVirtual"
          :getMediaItemAssets="getMediaItemAssets"
          :addAssetItemWithChanges="addAssetItemWithChanges"
          :removeAssetItemWithChanges="removeAssetItemWithChanges"
          :setAssetIsInProcessing="setAssetIsInProcessing"
          @updateMediaItem="$emit('updateMediaItem', $event)"
        />
        <AssetImageInspector
          ref="assetInfo"
          :source="imageAsset"
          :getMediaItemAssets="getMediaItemAssets"
          :setAssetIsInProcessing="setAssetIsInProcessing"
          :addAssetItemWithChanges="addAssetItemWithChanges"
          :removeAssetItemWithChanges="removeAssetItemWithChanges"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  GET_ASSET_LANGUAGE_REQUEST,
} from '../../../store/actions/media/mediaAsset';
import Loader from '../../common/Loader.vue';
import AssetImageUploader from '../../common/PosterImage/AssetImageUploader.vue';
import AssetImageInspector from '../inspector/AssetImageInspector.vue';
import iconWarning from '../../../assets/icons/icon-warning-localization.svg';
import { imageConstraints } from '../../../utils/validation/imagesValidation';

export default {
  name: 'AssetImagesList',
  components: {
    Loader,
    AssetImageUploader,
    AssetImageInspector,
  },
  props: {
    mediaItem: {
      type: Object,
      default: () => {},
    },
    getMediaItemAssets: {
      type: Function,
      default: () => {},
    },
    imageItemToHilight: {
      type: Object,
      default: null,
    },
    clearImageItemToHilight: {
      type: Function,
      default: () => {},
    },
    clearImagesTabWarn: {
      type: Function,
      default: () => {},
    },
    shouldWarnImagesTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconWarning,
      assetItemInProcessing: null,
      itemsWithChanges: [],
    };
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.mediaItem.isLoading,
      originalAssets: (state) => state.mediaItem.assets,
    }),
    assets() {
      // During transcoding we show editable virtual slots for images
      const isTranscoding = this.mediaItem.status === 'transcoding' && this.mediaItem.type !== 'live';
      if (isTranscoding) {
        return this.transcodingAssets;
      }

      // Base: take existing image assets
      const existingImageAssets = this.originalAssets.filter((item) => item.type === 'image');

      // Map by shortName for quick presence checks
      const byShortName = existingImageAssets.reduce((acc, item) => {
        if (item?.assetRole?.shortName) {
          acc[item.assetRole.shortName] = item;
        }
        return acc;
      }, {});

      // Define the desired poster slots order based on orientation
      const desiredSlots = this.mediaItem.orientation === 'vertical'
        ? ['posterImage9x16', 'posterImage2x3', 'posterImage16x9']
        : ['posterImage16x9', 'posterImage2x3'];

      // Helper to deep clone constraint template and set url if present on mediaItem
      const buildVirtualFromConstraint = (shortName) => {
        let key = null;
        if (shortName === 'posterImage16x9') key = 'assets-static-posterImage';
        if (shortName === 'posterImage2x3') key = 'assets-static-posterImage2x3';
        if (shortName === 'posterImage9x16') key = 'assets-static-posterImage9x16';
        const tmpl = JSON.parse(JSON.stringify(imageConstraints[key]));

        // Try to prefill URL from mediaItem computed fields if they exist
        if (shortName === 'posterImage16x9') {
          tmpl.url = this.mediaItem.posterImage || '';
        } else if (shortName === 'posterImage2x3') {
          tmpl.url = this.mediaItem.posterImage2x3 || '';
        } else if (shortName === 'posterImage9x16') {
          tmpl.url = this.mediaItem.posterImage9x16 || '';
        }

        return tmpl;
      };

      // Build normalized list: for each desired slot use real asset if exists, else virtual
      const normalizedPrimary = desiredSlots.map((shortName) => {
        if (byShortName[shortName]) {
          // Clone to avoid mutating original asset, add virtual flag
          return { ...byShortName[shortName], isVirtual: false };
        }
        const v = buildVirtualFromConstraint(shortName);
        v.isVirtual = true;
        return v;
      });

      // Append any other image assets that are not part of the primary poster slots
      const extraImages = existingImageAssets
        .filter((item) => (!desiredSlots.includes(item?.assetRole?.shortName)))
        .map((item) => ({ ...item, isVirtual: false }));

      return [...normalizedPrimary, ...extraImages];
    },
    hasUnsavedChanges() {
      return this.itemsWithChanges.length > 0;
    },
    transcodingAssets() {
      const posterImage = JSON.parse(JSON.stringify(imageConstraints['assets-static-posterImage']));
      posterImage.url = this.mediaItem.posterImage || '';
      posterImage.isVirtual = true;

      const posterImage2x3 = JSON.parse(JSON.stringify(imageConstraints['assets-static-posterImage2x3']));
      posterImage2x3.url = this.mediaItem.posterImage2x3 || '';
      posterImage2x3.isVirtual = true;

      const posterImage9x16 = JSON.parse(JSON.stringify(imageConstraints['assets-static-posterImage9x16']));
      posterImage9x16.url = this.mediaItem.posterImage9x16 || '';
      posterImage9x16.isVirtual = true;

      if (this.mediaItem.orientation === 'vertical') {
        return [posterImage9x16, posterImage2x3, posterImage];
      }

      return [posterImage, posterImage2x3];
    },
  },
  watch: {
    imageItemToHilight(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const element = this.$refs.assetImage.find((el) => el.source.id === newVal.id);
          if (element && element.$el && element.$el.scrollIntoView) {
            element.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          this.clearImageItemToHilight();
        });
      }
    },
    hasUnsavedChanges(newVal) {
      if (!newVal) {
        this.clearImagesTabWarn();
      }
    },
  },
  created() {
    this.$store.dispatch(GET_ASSET_LANGUAGE_REQUEST);
  },
  destroyed() {

  },
  methods: {
    setAssetIsInProcessing(item) {
      this.assetItemInProcessing = item;
    },
    addAssetItemWithChanges(item) {
      if (this.itemsWithChanges.includes(item)) {
        this.itemsWithChanges = this.itemsWithChanges.filter((el) => el !== item);
      } else {
        this.itemsWithChanges.push(item);
      }
    },
    removeAssetItemWithChanges(item) {
      this.itemsWithChanges = this.itemsWithChanges.filter((el) => el !== item);
    },
  },
};
</script>

<style scoped>
.images-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  height: 100%;
}
.no-images{
  position: relative;
  width: 100%;
  height: 50vh;
  display: flex;
  justify-content: center;
  padding-top: 40px;
}
.image-container {
  width: fit-content;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  border: 1px solid #43434d;
  padding: 30px;
  gap: 20px;
  position: relative;
}
.unsaved-changes-warning {
  color: #F2C94C;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #F2C94C;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
